import { Button, Menu, MenuItem } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { List } from 'react-virtualized';
import styles from './BOSFilterSelect.module.css';

const BOSFilterSelect = forwardRef(
  (
    {
      labelText,
      listData = [],
      onItemSelect = () => {},
      idField = 'id',
      labelField = 'name',
      labelFunction,
      searchField,
      searchFunction,
      placeholder = 'Select...',
      fill = false,
      defaultSelectedId,
      icon,
      floatSelected = true,
      hideClearButton = false,
      matchTargetWidth = false,
      simple = false,
      useKeyValueFields = false,
      useLargeLabel = false,
      readOnly = false,
      isRequired = false,
      width = 400,
      onBlur = () => {},
      inactive = false,
      inactiveStyleFunction,
    },
    ref,
  ) => {
    const [fieldNames] = useState(
      useKeyValueFields
        ? { idField: 'key', labelField: 'value' }
        : { idField, labelField, searchField },
    );
    const [selectedItem, setSelectedItem] = useState({});
    const [defaultSelectedItem, setDefaultSelectedItem] = useState({});
    const inputRef = useRef();

    useEffect(() => {
      if (defaultSelectedId && listData) {
        setDefaultSelectedItem(
          listData.find(
            (item) => item[fieldNames.idField] === defaultSelectedId,
          ),
        );
      } else {
        setDefaultSelectedItem(null);
      }
    }, [defaultSelectedId, listData]);

    useEffect(() => {
      if (defaultSelectedItem) {
        setSelectedItem(defaultSelectedItem);
      } else {
        setSelectedItem(null);
      }
    }, [defaultSelectedItem]);

    const itemListRenderer = ({
      filteredItems,
      itemsParentRef,
      menuProps,
      activeItem,
      renderItem,
      query,
    }) => {
      if (readOnly) return;
      let height = filteredItems.length * 30;
      height = height <= 300 ? height : 300;
      return (
        <Menu role='listbox' ulRef={itemsParentRef} {...menuProps}>
          {filteredItems.length === 0 && (
            <MenuItem
              className={styles.no_content}
              text={query ? `No items matching '${query}'...` : 'No items'}
              icon={'blank'}
            />
          )}
          <List
            className={styles.list}
            height={height}
            rowHeight={30}
            rowCount={filteredItems.length}
            overscanRowCount={50}
            width={width}
            rowRenderer={getRowRenderer(filteredItems, renderItem)}
          />
        </Menu>
      );
    };

    const getRowRenderer = (filteredItems, renderItem) => {
      return ({ index, isScrolling, key, style }) => {
        let item = filteredItems[index];
        return (
          <div className={styles.row} key={key} style={style}>
            {renderItem(item, index)}
          </div>
        );
      };
    };

    const itemRenderer = (
      item,
      { index, handleClick, modifiers, handleFocus },
    ) => {
      if (!modifiers.matchesPredicate) {
        return null;
      }

      return (
        <MenuItem
          key={index}
          className={
            inactiveStyleFunction && inactiveStyleFunction(item)
              ? styles.inactive_item
              : ''
          }
          selected={item === selectedItem}
          active={!simple && modifiers.active}
          icon={simple ? undefined : item === selectedItem ? 'tick' : 'blank'}
          shouldDismissPopover={false}
          text={
            labelFunction
              ? labelFunction(item)
              : item[fieldNames.labelField] || ''
          }
          onClick={handleClick}
          onFocus={handleFocus}
        />
      );
    };

    const filterPredicate = (query, item) => {
      let fieldToSearchBy = fieldNames.searchField || fieldNames.labelField;
      let itemSearchText = searchFunction
        ? searchFunction(item)
        : item[fieldToSearchBy];
      return itemSearchText?.toLowerCase().includes(query.toLowerCase());
    };

    const onItemSelected = (item) => {
      setSelectedItem(item);
      onItemSelect(item);
    };

    const chevronDown = (!selectedItem || isRequired) && (
      <Button
        minimal
        icon={'chevron-down'}
        onClick={(e) => {
          ref?.current && ref.current.focus();
          inputRef?.current && inputRef.current.focus();
        }}
      />
    );

    const clearButton = !hideClearButton &&
      selectedItem &&
      !isRequired &&
      Object.hasOwn(selectedItem, fieldNames.idField) && (
        <Button
          minimal
          icon={'cross'}
          onClick={() => {
            onItemSelected(null);
          }}
        />
      );

    let sortedArray =
      floatSelected && listData
        ? [
            ...listData.filter((item) => item === selectedItem),
            ...listData.filter((item) => item !== selectedItem),
          ]
        : listData;

    return (
      <div
        style={{ textAlign: 'left', width: '100%' }}
        className={inactive ? styles.inactive : ''}
        onClick={onBlur}>
        {labelText && <span style={{ fontSize: 'small' }}>{labelText}</span>}
        <Suggest
          itemListRenderer={itemListRenderer}
          itemRenderer={itemRenderer}
          items={sortedArray}
          itemsEqual={fieldNames.idField}
          itemPredicate={filterPredicate}
          inputValueRenderer={(item) =>
            (item && labelFunction
              ? labelFunction(item)
              : item[fieldNames.labelField]) || ''
          }
          onItemSelect={onItemSelected}
          defaultSelectedItem={defaultSelectedItem}
          selectedItem={selectedItem}
          popoverProps={{
            minimal: true,
            ...(matchTargetWidth && { matchTargetWidth }),
          }}
          popoverContentProps={{
            className: styles.menu_simple,
          }}
          fill={fill}
          inputProps={{
            inputRef: ref || inputRef,
            placeholder:
              isRequired && !selectedItem && !inactive
                ? '(Required)'
                : placeholder,
            rightElement: clearButton || chevronDown,
            leftIcon: icon,
            ...(useLargeLabel && {
              style: {
                fontWeight: 600,
                fontSize: 22,
                lineHeight: 25,
              },
            }),
            ...(isRequired &&
              !selectedItem &&
              !inactive && {
                intent: 'danger',
              }),
            ...(isRequired &&
              !selectedItem &&
              !inactive && {
                className: styles.required_input,
              }),
            readOnly: readOnly,
          }}
          filterable
          scrollToActiveItem
          resetOnClose={false}
          resetOnQuery={false}
        />
      </div>
    );
  },
);

BOSFilterSelect.displayName = 'BOSFilterSelect';

BOSFilterSelect.propTypes = {
  labelText: PropTypes.string,
  listData: PropTypes.array,
  onItemSelect: PropTypes.func,
  idField: PropTypes.string,
  labelField: PropTypes.string,
  labelFunction: PropTypes.func,
  searchField: PropTypes.string,
  searchFunction: PropTypes.func,
  placeholder: PropTypes.string,
  fill: PropTypes.bool,
  defaultSelectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.string,
  floatSelected: PropTypes.bool,
  hideClearButton: PropTypes.bool,
  matchTargetWidth: PropTypes.bool,
  simple: PropTypes.bool,
  useKeyValueFields: PropTypes.bool,
  useLargeLabel: PropTypes.bool,
  readOnly: PropTypes.bool,
  isRequired: PropTypes.bool,
  width: PropTypes.number,
  onBlur: PropTypes.func,
  inactive: PropTypes.bool,
  inactiveStyleFunction: PropTypes.func,
};

export default BOSFilterSelect;
